import type { FC } from 'react'
import { useMemo } from 'react'
import styled from '@emotion/styled'
import type { Document } from '@contentful/rich-text-types'
import { BLOCKS } from '@contentful/rich-text-types'
import type { Options as RichTextRenderOptions } from '@contentful/rich-text-react-renderer'

import Typography from '@segment/matcha/components/Typography'

import RichText, { ColoredTypography, defaultOptions } from '@segment/matcha/recipes/RichText'

import type { WithoutMethodsDeep } from '@segment/utils/types'

interface Section {
  heading: string
  id: string
  content: WithoutMethodsDeep<Document>
}
interface ArticleContentProps {
  sections: Section[]
}
const ArticleContent: FC<ArticleContentProps> = ({ sections }) => (
  <Wrapper>
    {sections.map((section) => (
      <ArticleSection section={section} key={section.id} />
    ))}
  </Wrapper>
)

export default ArticleContent

interface ArticleSectionProps {
  section: Section
}
const ArticleSection: FC<ArticleSectionProps> = ({ section }) => {
  const options = useMemo<RichTextRenderOptions>(
    () => ({
      renderNode: {
        [BLOCKS.UL_LIST]: (_, children) => <List>{children}</List>,
        [BLOCKS.PARAGRAPH](_, children) {
          switch (section.id) {
            case `resources-and-links`:
              return (
                <Paragraph variant="pLarge" tag="p">
                  {children}
                </Paragraph>
              )
            case `episode-timestamps`:
              return (
                <ColoredTypography variant="pMedium" tag="p">
                  {children}
                </ColoredTypography>
              )
            default:
              return defaultOptions?.renderNode?.[BLOCKS.PARAGRAPH](_, children)
          }
        },
      },
    }),
    [section.id]
  )

  return (
    <section key={section.heading}>
      <Typography variant="h2" tag="h2" id={section.id}>
        {section.heading}
      </Typography>
      <RichText data={section.content} options={options} />
    </section>
  )
}

const Wrapper = styled.article`
  display: grid;
  ${({ theme }) => theme.breakpoints.up(`md`)} {
    gap: 32px;
  }
`

const List = styled.ul`
  display: grid;
  gap: 16px;
  padding-left: 27px;
  color: ${({ theme }) => theme.palette.brand.darkAcai[500]};
`

const Paragraph = styled(ColoredTypography)`
  margin: 0;
`
